/**
 * Legacy adapter: compile the old property bag into a theme.
 *
 * Sites store their look as loose `properties` keys (color_text, header_title,
 * logo_link_page_id, ...). Rather than migrating every database, the public
 * renderer compiles those into a theme on read. A site that never opens the
 * Appearance editor therefore keeps rendering exactly as before, and no theme
 * JSON is ever written for it.
 *
 * Both SSR (lib/server.js) and hydration (lib/client.js) must go through here,
 * or the two would compute different values and React would report a mismatch.
 */

import { DEFAULT_THEME } from './defaults.js';
import { servedThemeEntries } from './document.js';
import { ensureHeaderBlocks } from './header-blocks.js';

/** Property keys that used to hold the site colours. */
export const LEGACY_COLOR_PROPERTIES = {
    background: 'color_background',
    text: 'color_text',
    sideMenuText: 'color_side_menu_text',
    sideMenuBackground: 'color_side_menu_background',
};

/** Property keys that used to hold brand text and the logo link. */
export const LEGACY_BRAND_PROPERTIES = {
    siteTitle: 'site_title',
    metaTitle: 'meta_title',
    headerTitle: 'header_title',
    hrefPageId: 'logo_link_page_id',
};

function cloneDefaults() {
    return JSON.parse(JSON.stringify(DEFAULT_THEME));
}

/**
 * Build the theme a site renders with when it has no saved theme document.
 *
 * @param {Object} properties - content.properties from the database
 * @returns {Object} A complete theme
 */
export function themeFromLegacy(properties = {}) {
    const theme = cloneDefaults();
    const props = properties || {};

    Object.entries(LEGACY_COLOR_PROPERTIES).forEach(([token, key]) => {
        // Empty string means "unset" in the old editor, so fall back to the default.
        if (props[key]) theme.colors[token] = props[key];
    });

    theme.brand.siteTitle = props[LEGACY_BRAND_PROPERTIES.siteTitle] || '';
    theme.brand.metaTitle = props[LEGACY_BRAND_PROPERTIES.metaTitle] || '';
    theme.brand.headerTitle = props[LEGACY_BRAND_PROPERTIES.headerTitle] || '';
    theme.brand.logo.hrefPageId = props[LEGACY_BRAND_PROPERTIES.hrefPageId] || null;

    return theme;
}

/**
 * The theme the public site should render with.
 *
 * Prefers the published live theme when one exists, otherwise compiles the
 * legacy properties. Never writes anything.
 *
 * @param {Object} content - the object returned by read()
 * @returns {Object} { theme, source } where source is 'published' or 'legacy'
 */
export function resolveLiveTheme(content = {}) {
    const document = content?.theme;
    const liveId = document?.liveId;
    const themes = Array.isArray(document?.themes) ? document.themes : [];
    const live = liveId ? themes.find((entry) => entry.id === liveId) : null;

    if (live) {
        // A published theme was compiled from these same defaults, so a partial
        // entry still resolves to something complete.
        const theme = ensureHeaderBlocks(mergeTheme(themeFromLegacy(content?.properties), live));
        return { theme, source: 'published' };
    }

    return { theme: ensureHeaderBlocks(themeFromLegacy(content?.properties)), source: 'legacy' };
}

/**
 * Every theme a visitor might be served, resolved and ready to compile.
 *
 * One entry (the live theme) in the normal case. More only when an A/B pool has
 * been set up, and the live theme is always first so it stays the `:root` default.
 *
 * @param {Object} content - the object returned by read()
 * @returns {Array} [{ id, theme }]
 */
export function resolveServedThemes(content = {}) {
    const base = themeFromLegacy(content?.properties);
    const entries = servedThemeEntries(content?.theme);

    if (entries.length === 0) {
        return [{ id: 'legacy', theme: ensureHeaderBlocks(base) }];
    }

    return entries.map((entry) => ({
        id: entry.id,
        theme: ensureHeaderBlocks(mergeTheme(base, entry)),
    }));
}

/**
 * Deep-merge a partial theme over a base, array values replacing wholesale.
 * @param {Object} base
 * @param {Object} patch
 * @returns {Object} A new merged theme
 */
export function mergeTheme(base, patch) {
    if (!patch || typeof patch !== 'object') return base;

    const out = Array.isArray(base) ? [...base] : { ...base };

    Object.entries(patch).forEach(([key, value]) => {
        // Metadata on a theme entry, not part of the rendered theme.
        if (['id', 'name', 'status', 'createdAt', 'updatedAt'].includes(key)) return;

        if (value && typeof value === 'object' && !Array.isArray(value)
            && out[key] && typeof out[key] === 'object' && !Array.isArray(out[key])) {
            out[key] = mergeTheme(out[key], value);
        } else if (value !== undefined) {
            out[key] = value;
        }
    });

    return out;
}

export default {
    themeFromLegacy,
    resolveLiveTheme,
    resolveServedThemes,
    mergeTheme,
    LEGACY_COLOR_PROPERTIES,
    LEGACY_BRAND_PROPERTIES,
};
